import { useEffect, useState } from "react";
import { WorkspaceCardData } from "./Interfaces/WorkspaceCard";
import WorkspaceCard from "./WorkspaceCard";
import axios from "axios";

interface FeaturedWorkspacesRequest {
  location?: string;
  latitude?: number;
  longitude?: number;
  radiusKm?: number;
  limit?: number;
}

interface FeaturedWorkspacesProps {
  location?: string;
  latitude?: number;
  longitude?: number;
}

export default function FeaturedWorkspaces({
  location,
  latitude,
  longitude,
}: FeaturedWorkspacesProps) {
  const [featuredWorkspaces, setFeaturedWorkspaces] = useState<
    WorkspaceCardData[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchFeaturedWorkspaces();
  }, [location, latitude, longitude]);

  const fetchFeaturedWorkspaces = async () => {
    try {
      setLoading(true);
      const api = axios.create({
        baseURL: "/",
      });

      const payload: FeaturedWorkspacesRequest = {
        location: location,
        latitude: latitude,
        longitude: longitude,
        radiusKm: 50, // 50km radius
        limit: 8, // Show up to 8 featured workspaces
      };

      const response = await api.post(
        "/api/Workspace/getFeaturedWorkspaces",
        payload
      );

      // Handle both array and object responses
      const data = response.data;
      const workspacesData = Array.isArray(data)
        ? data
        : data?.workspaces || [];

      setFeaturedWorkspaces(workspacesData as WorkspaceCardData[]);
      setError(null);
    } catch (err) {
      console.error("Error fetching featured workspaces:", err);
      setError("Failed to load featured workspaces");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Featured Workspaces
            </h2>
            <p className="text-gray-600">
              Discover our top-rated and recommended workspaces
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-gray-300 rounded-lg h-48 mb-4"></div>
                <div className="space-y-2">
                  <div className="bg-gray-300 h-4 rounded"></div>
                  <div className="bg-gray-300 h-4 rounded w-3/4"></div>
                  <div className="bg-gray-300 h-4 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex">
              <svg
                className="w-5 h-5 text-red-400 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading featured workspaces
                </h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading || error || featuredWorkspaces.length === 0) {
    return null; // Don't render anything if loading, error, or no featured workspaces
  }

  return (
    <div className="py-8" style={{ backgroundColor: "var(--background)" }}>
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
            <svg
              className="w-4 h-4 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            Featured Workspaces
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            {location ? `Featured in ${location}` : "Our Top Recommendations"}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover the highest-rated and most recommended workspaces,
            carefully selected for their exceptional quality and customer
            satisfaction.
          </p>
        </div>

        {/* Featured Workspaces Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {featuredWorkspaces.map((workspace, index) => (
            <div key={workspace.workspaceId} className="relative">
              {/* Featured Badge */}
              <div className="absolute top-3 left-3 z-10">
                <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                  <svg
                    className="w-3 h-3 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  Featured
                </div>
              </div>

              {/* Ranking Badge for top 3 */}
              {index < 3 && (
                <div className="absolute top-3 right-3 z-10">
                  <div
                    className={`text-white px-2 py-1 rounded-full text-xs font-bold ${
                      index === 0
                        ? "bg-yellow-500"
                        : index === 1
                        ? "bg-gray-400"
                        : "bg-orange-600"
                    }`}
                  ></div>
                </div>
              )}

              <WorkspaceCard {...workspace} />
            </div>
          ))}
        </div>

        {/* View All Featured Button */}
        {featuredWorkspaces.length >= 8 && (
          <div className="text-center mt-8">
            <button className="bg-[var(--secondary)] text-white px-8 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl">
              View All Featured Workspaces
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
